import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AdyenDropinWrapper } from './adyen-dropin-wrapper';
import { AutoPayNoPaymentMethodsContainer } from './autopay-no-payment-methods-container';
import { AutoPaySettingsContainer } from './autopay-settings-container';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import {
  PaymentMethodOption,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { PaymentMethodSelector } from './payment-method-selector';
import { SCREEN_PADDING } from '@/styles';
import { StyleSheet, View } from 'react-native';
import { useAutopaySettings } from '@/hooks/payments/use-autopay-settings';
import { useInitiatePlaid } from '@/hooks/use-initiate-plaid';
import { useToasts } from '@/contexts/toast-context';

enum AutoPayActionSheetView {
  AddPaymentMethod = 'ADD_PAYMENT_METHOD',
  AutoPay = 'AUTO_PAY',
  Loading = 'LOADING',
  SelectPaymentMethod = 'SELECT_PAYMENT_METHOD',
}

export interface Props {
  onClose: () => void;
}

export const AutoPayActionSheet: React.FC<Props> = ({ onClose }) => {
  const {
    plaidEnabled,
    refetchPaymentContext,
    setPaymentContextState,
    transactionFeeMap,
    autoPaySelfServeEnabled,
    savingAutoPaySetting,
    selectAutoPaySettings,
    selectedPaymentMethod,
  } = usePaymentsContext();
  const {
    availableAutoPaySettings,
    hasSavedAutoPaySetting,
    selectedAutoPaySettingUUID,
    setSelectedAutoPaySettingUUID,
    shouldSaveAutoPaySetting,
  } = useAutopaySettings();
  const { showErrorToast } = useToasts();
  const initiatePlaid = useInitiatePlaid();

  const onSaveAutoPaySetting = (autoPaySettingUuid: string) => {
    try {
      selectAutoPaySettings(autoPaySettingUuid);
      setSelectedAutoPaySettingUUID(autoPaySettingUuid);
    } catch (e) {
      showErrorToast();
    }
  };

  return (
    <CombinedActionSheet
      initialRoute={AutoPayActionSheetView.AutoPay}
      onClose={onClose}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case AutoPayActionSheetView.AddPaymentMethod: {
            return (
              <AdyenDropinWrapper
                onSaveSuccess={() => jumpTo(AutoPayActionSheetView.AutoPay)}
                requireSavedPaymentMethod
                transactionFeeMap={transactionFeeMap}
              />
            );
          }
          case AutoPayActionSheetView.AutoPay: {
            return (
              <>
                {!selectedPaymentMethod ? (
                  <AutoPayNoPaymentMethodsContainer
                    onAddPaymentMethod={() => {
                      jumpTo(AutoPayActionSheetView.AddPaymentMethod);
                    }}
                  />
                ) : (
                  <AutoPaySettingsContainer
                    autoPaySelfServeEnabled={autoPaySelfServeEnabled}
                    availableAutoPaySettings={availableAutoPaySettings}
                    hasSavedAutoPaySetting={hasSavedAutoPaySetting}
                    onChangePaymentMethod={() => {
                      jumpTo(AutoPayActionSheetView.SelectPaymentMethod);
                    }}
                    onSaveAutoPaySetting={onSaveAutoPaySetting}
                    onSelectAutoPaySetting={setSelectedAutoPaySettingUUID}
                    savingAutoPaySetting={savingAutoPaySetting}
                    selectedAutoPaySettingUUID={selectedAutoPaySettingUUID}
                    selectedPaymentMethod={selectedPaymentMethod}
                    shouldSaveAutoPaySetting={shouldSaveAutoPaySetting}
                  />
                )}
              </>
            );
          }
          case AutoPayActionSheetView.Loading: {
            return (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" />
              </View>
            );
          }
          case AutoPayActionSheetView.SelectPaymentMethod: {
            return (
              <View style={styles.paymentMethodSelector}>
                <PaymentMethodSelector
                  onAddMethod={() =>
                    jumpTo(AutoPayActionSheetView.AddPaymentMethod)
                  }
                  onConnectBankAccount={
                    plaidEnabled
                      ? async () => {
                          try {
                            jumpTo(AutoPayActionSheetView.Loading);
                            await initiatePlaid();

                            // Once the browser is closed, refresh the payment
                            // methods in case a new one was added for Plaid
                            await refetchPaymentContext();
                            jumpTo(AutoPayActionSheetView.SelectPaymentMethod);
                          } catch (e) {
                            showErrorToast();
                            jumpTo(AutoPayActionSheetView.SelectPaymentMethod);
                          }
                        }
                      : undefined
                  }
                  onSelectMethod={(paymentMethod?: PaymentMethodOption) => {
                    setPaymentContextState({
                      selectedPaymentMethod: paymentMethod,
                    });
                    jumpTo(AutoPayActionSheetView.AutoPay);
                  }}
                />
              </View>
            );
          }
          default: {
            return null;
          }
        }
      }}
      routes={{
        [AutoPayActionSheetView.AddPaymentMethod]: {
          previousKey: null,
          title: i18n.t('add_payment_method'),
        },
        [AutoPayActionSheetView.AutoPay]: {
          previousKey: null,
          title: i18n.t('autopay.title'),
        },
        [AutoPayActionSheetView.Loading]: {
          previousKey: null,
          title: i18n.t('loading'),
        },
        [AutoPayActionSheetView.SelectPaymentMethod]: {
          previousKey: null,
          title: i18n.t('select_payment_method'),
        },
      }}
    />
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    paddingBottom: 48,
    paddingTop: 32,
  },
  paymentMethodSelector: {
    marginHorizontal: -SCREEN_PADDING,
  },
});
